import { useTranslation } from '@splitfiesta/i18n';
import { APP_STORE_URLS } from '@splitfiesta/shared';

import appStoreDe from '@/assets/badges/de/app-store.svg';
import googlePlayDe from '@/assets/badges/de/google-play.png';
import appStoreEn from '@/assets/badges/en/app-store.svg';
import googlePlayEn from '@/assets/badges/en/google-play.png';
import appStoreEs from '@/assets/badges/es/app-store.svg';
import googlePlayEs from '@/assets/badges/es/google-play.png';
import appStoreFr from '@/assets/badges/fr/app-store.svg';
import googlePlayFr from '@/assets/badges/fr/google-play.png';
import appStorePt from '@/assets/badges/pt/app-store.svg';
import googlePlayPt from '@/assets/badges/pt/google-play.png';

export type BadgeStore = 'ios' | 'android';

interface StoreBadgeProps {
  store: BadgeStore;
  /** Extra classes on the link. */
  className?: string;
  /** `md` (h-12) for hero/footer placements, `sm` (h-10) for inline nudges. */
  size?: 'md' | 'sm';
  onClick?: () => void;
}

/** Localized official badges, keyed by language. Apple ships tight SVGs;
 *  Google's localized asset is a PNG with built-in clear space. */
const BADGES: Record<string, Record<BadgeStore, string>> = {
  en: { ios: appStoreEn, android: googlePlayEn },
  es: { ios: appStoreEs, android: googlePlayEs },
  fr: { ios: appStoreFr, android: googlePlayFr },
  pt: { ios: appStorePt, android: googlePlayPt },
  de: { ios: appStoreDe, android: googlePlayDe },
};

const LABEL_KEY: Record<BadgeStore, string> = {
  ios: 'storeButtons.appStore',
  android: 'storeButtons.googlePlay',
};

/** One official App Store / Google Play badge in the active UI language. */
export function StoreBadge({
  store,
  className = '',
  size = 'md',
  onClick,
}: StoreBadgeProps) {
  const { t, i18n } = useTranslation('common');
  const lang = (i18n?.language || 'en').split('-')[0];
  const src = (BADGES[lang] ?? BADGES.en)[store];
  const label = t(LABEL_KEY[store]);

  return (
    <a
      href={APP_STORE_URLS[store]}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`store-button-${store}`}
      className={`inline-flex shrink-0 ${className}`}
      onClick={onClick}
    >
      <img
        src={src}
        alt={label}
        className={size === 'sm' ? 'h-10 w-auto' : 'h-12 w-auto'}
      />
    </a>
  );
}
